import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import { authApi } from '../features/auth/api';
import { useAuth } from '../features/auth/context';
import { ensureGuestSession, setGuestTripId, openGuestWall } from '../features/auth/guest';
import { useDocumentTitle } from '../shared/useDocumentTitle';
import { createTrip, updateTrip, fetchTripDetail, generateAlternative } from '../features/my-trips/api';
import { allowedStrategies, NIGHT_TRAIN_OPTION, Strategy } from '../features/my-trips/strategies';
import { fetchNightTrainFormOption } from '../features/journeys/api';
import LLMLoadingSpinner from '../components/LLMLoadingSpinner';
import { TripParams, TripPlace, TripStage } from '../features/my-trips/types';
import CitySearchInput from '../features/geocoding/CitySearchInput';
import { CityResult, searchPlacesWithCountries } from '../features/geocoding/api';
import { usesSharedTransport } from '../features/transport/TransportFields';
import FormField from '../components/ui/FormField';
import Input from '../components/ui/Input';
import DateRangeFields from '../components/ui/DateRangeFields';
import { isDateRangeValid, DATE_RANGE_MESSAGE } from '../shared/dateRange';
import Dropdown from '../components/ui/Dropdown';
import Textarea from '../components/ui/Textarea';
import Button from '../components/ui/button';
import Switch from '../components/ui/Switch';
import Tooltip from '../components/ui/Tooltip';
import ConfirmationDialog from '../components/ConfirmationDialog';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// New / Edit trip form, v2 (two-tier redesign, 2026-07-14 — mockup A from the
// trip-form funnel work). One page for both create (/trips/new) and edit
// (/trips/:id/edit). Tier 1 (always visible): where, when (dates OR a rough
// duration preset), transport. Tier 2 (collapsed on
// create, expanded on edit): title (auto-derived from the route when left
// empty), stage, travelers, round trip, pace, a single free-text brief, and
// friends. `durationPreset` and `pace` set the journey's stay budget
// (backend/utils/tripPacing.js); the old "How many destinations?" chip
// (`destinationRange`) was retired with pacing (backlog #52). Location fields
// accept whole countries (searchPlacesWithCountries).

// Axios/fetch abort surfaces as a canceled error — used to skip the error toast
// when the user themselves cancelled the post-edit regeneration.
const isAbortError = (e: any): boolean =>
  e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError';

type Pace = 'relaxed' | 'balanced' | 'fast';
type DurationPreset = NonNullable<TripParams['durationPreset']>;

const STAGE_OPTIONS = [
  { value: 'idea', label: 'Idea' },
  { value: 'organizing', label: 'Organizing' },
  { value: 'booked', label: 'Booked' },
];

const DURATION_PRESET_OPTIONS: Array<{ value: DurationPreset; label: string }> = [
  { value: 'weekend', label: 'A weekend' },
  { value: 'one_week', label: '1 week' },
  { value: 'two_weeks', label: '2 weeks' },
  { value: 'one_month', label: 'A month' },
  { value: 'flexible', label: 'Fully flexible' },
];

// Nights per place for each pace: the SAME numbers the backend uses to size the
// journey (NIGHTS_PER_STAY in backend/utils/tripPacing.js). Keep them in sync.
const PACE_OPTIONS: Array<{ value: Pace; label: string; nights: number }> = [
  { value: 'relaxed', label: 'Relaxed', nights: 7 },
  { value: 'balanced', label: 'Balanced', nights: 4 },
  { value: 'fast', label: 'Fast', nights: 2 },
];

// Transport chips: one row, two vocabularies (same values TransportFields
// uses). Shared modes multi-select; personal motorised modes are mutually
// exclusive with shared AND each other (usesSharedTransport rule); bicycle
// coexists with shared modes.
const SHARED_CHIPS = [
  { value: 'train', label: 'Train', emoji: '🚆' },
  // "Night train" next to "Train", so travellers see it is offered (night
  // trains spec §4; an experiment behind the backend's
  // `nightTrain.formOptionEnabled`, read on mount). "Train" alone allows
  // night trains too; this one asks for them. Twin of
  // backend/services/nightTrains/formOption.js.
  { value: NIGHT_TRAIN_OPTION, label: 'Night train', emoji: '🌙' },
  { value: 'bus', label: 'Bus', emoji: '🚌' },
  { value: 'car_sharing', label: 'Car Sharing', emoji: '🚗' },
  { value: 'airplane', label: 'Airplane', emoji: '✈️' },
  { value: 'boat', label: 'Boat', emoji: '⛴️' },
];
const PERSONAL_CHIPS = [
  { value: 'car', label: 'Car', emoji: '🚗' },
  { value: 'camper_van', label: 'Camper van', emoji: '🚐' },
  { value: 'motorcycle', label: 'Motorcycle', emoji: '🏍️' },
  { value: 'bicycle', label: 'Bicycle', emoji: '🚲' },
  { value: 'walking', label: 'Walking', emoji: '🚶' },
];
const MOTORISED_PERSONAL = ['car', 'camper_van', 'motorcycle'];

interface Friend {
  id: string;
  name: string;
  city?: string;
  country?: string;
}

// One destination row keeps a stable client key so CitySearchInput's internal
// state survives add/remove of sibling rows.
interface DestRow {
  key: number;
  place: TripPlace | null;
}

const cityResultToPlace = (c: CityResult): TripPlace => ({
  city: c.name,
  country: c.country,
  latitude: c.lat,
  longitude: c.lng,
  displayName: c.displayName,
  kind: c.kind,
});

const placeLabel = (p: TripPlace | null | undefined): string =>
  p ? (p.displayName || [p.city, p.country].filter(Boolean).join(', ')) : '';

// ISO date (or null) → yyyy-mm-dd for <input type="date">.
const toDateInput = (iso: string | null | undefined): string =>
  iso ? String(iso).slice(0, 10) : '';

// Auto-derived title when the user leaves the (optional) title empty: named
// from the route so a fresh trip never blocks on naming. Editable later via
// the Edit form.
const deriveTitle = (places: TripPlace[]): string => {
  if (places.length === 0) return '';
  if (places.length === 1) return `Trip to ${places[0].city}`;
  return `${places[0].city} to ${places[places.length - 1].city}`;
};

// --- Small mockup-A controls (local by design: trip-form styling, not shared) --

const Chip: React.FC<{
  label: string;
  emoji?: string;
  active: boolean;
  onClick: () => void;
}> = ({ label, emoji, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
      borderRadius: 999, fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
      border: active ? '1.5px solid #157a55' : '1px solid rgba(20,40,30,0.15)',
      background: active ? '#e7f1ec' : '#ffffff',
      color: active ? '#0e5c40' : '#16241d',
      transition: 'background .12s, border-color .12s, color .12s',
    }}
  >
    {emoji && <span style={{ fontSize: 15 }}>{emoji}</span>}
    {label}
  </button>
);

const ChipRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{children}</div>
);

// Section card with the mockup's uppercase eyebrow + serif question label.
const SectionCard: React.FC<{
  eyebrow: string;
  icon: React.ReactNode;
  question?: string;
  children: React.ReactNode;
}> = ({ eyebrow, icon, question, children }) => (
  <div style={{ background: '#ffffff', borderRadius: 16, padding: 16, marginBottom: 12 }}>
    <p
      style={{
        display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 700,
        letterSpacing: '0.09em', textTransform: 'uppercase', color: '#0e5c40', margin: '0 0 12px',
      }}
    >
      {icon}
      {eyebrow}
    </p>
    {question && (
      <p style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 17, fontWeight: 600, margin: '0 0 10px', color: '#16241d' }}>
        {question}
      </p>
    )}
    {children}
  </div>
);

const hintStyle: React.CSSProperties = { fontSize: 12.5, color: '#6c7872', margin: '8px 0 0', lineHeight: 1.4 };
const groupLabelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: '#16241d', margin: '0 0 8px' };
const linkyStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: '#157a55', fontSize: 13.5, fontWeight: 600,
  cursor: 'pointer', padding: 0, textDecoration: 'underline', textUnderlineOffset: 3,
};

const Stepper: React.FC<{ value: number; min: number; max: number; onChange: (n: number) => void; ariaLabel: string }> =
  ({ value, min, max, onChange, ariaLabel }) => (
    <div
      role="group"
      aria-label={ariaLabel}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 2, background: '#f4f6f3', borderRadius: 999, padding: 4 }}
    >
      <button
        type="button"
        aria-label={`Fewer ${ariaLabel.toLowerCase()}`}
        onClick={() => onChange(Math.max(min, value - 1))}
        style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'transparent', color: '#157a55', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
      >
        −
      </button>
      <span style={{ minWidth: 28, textAlign: 'center', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      <button
        type="button"
        aria-label={`More ${ariaLabel.toLowerCase()}`}
        onClick={() => onChange(Math.min(max, value + 1))}
        style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'transparent', color: '#157a55', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
      >
        +
      </button>
    </div>
  );

// --- Sortable location row (behaviour unchanged from v1) ---------------------
// Reordering uses @dnd-kit/sortable (accessible + touch). A hook can't be
// called per-item inside a map, so each row is its own component that calls
// useSortable. Only the grip is a drag handle, so the city field stays
// clickable/typable. The first row (index 0) is the origin — badged "Start";
// the rest are numbered destinations, all in one reorderable list.
interface SortableDestRowProps {
  row: DestRow;
  index: number;
  isFirst: boolean;
  multiple: boolean;
  /** Highlight this row's input red (submit failed the min-locations rule). */
  invalid: boolean;
  focusKey: number | null;
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>, index: number, row: DestRow) => void;
  onSelectPlace: (key: number, place: TripPlace | null) => void;
  onRemove: (key: number) => void;
}

const SortableDestRow: React.FC<SortableDestRowProps> = ({ row, index, isFirst, multiple, invalid, focusKey, onKeyDown, onSelectPlace, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.key });
  const style: React.CSSProperties = {
    display: 'flex', gap: 8, alignItems: 'flex-start',
    transform: CSS.Transform.toString(transform),
    transition,
    position: 'relative',
    zIndex: isDragging ? 1 : undefined,
    opacity: isDragging ? 0.6 : 1,
  };
  const label = isFirst ? 'starting point' : `destination ${index}`;
  return (
    <div ref={setNodeRef} style={style} onKeyDown={(e) => onKeyDown(e, index, row)}>
      {multiple && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={`Reorder ${label}`}
          title="Drag to reorder"
          style={{
            flexShrink: 0, width: 26, height: 38, display: 'flex', alignItems: 'center',
            justifyContent: 'center', cursor: 'grab', color: '#9aa6a0', fontSize: 16,
            userSelect: 'none', border: 'none', background: 'transparent', padding: 0,
            touchAction: 'none', // let the pointer sensor own touch on the grip
          }}
        >
          ⠿
        </button>
      )}
      {/* Order badge: green "Start" on the origin, muted number on each stop —
          keeps the list readable and inputs aligned as rows are reordered. */}
      <div
        style={{
          flexShrink: 0, height: 38, minWidth: 42, padding: '0 8px', display: 'flex',
          alignItems: 'center', justifyContent: 'center', borderRadius: 10, fontSize: 12,
          fontWeight: 700, whiteSpace: 'nowrap',
          background: isFirst ? '#e7f1ec' : '#f4f6f3',
          color: isFirst ? '#0e5c40' : '#6c7872',
        }}
      >
        {isFirst ? 'Start' : index}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <CitySearchInput
          initialLabel={placeLabel(row.place)}
          placeholder={isFirst ? 'Leaving from, e.g. Lisbon' : 'To, e.g. Rome or Italy'}
          autoFocus={row.key === focusKey}
          invalid={invalid}
          searchFn={searchPlacesWithCountries}
          selectionRequiredMessage="Please pick a place from the list."
          onSelect={(c) => onSelectPlace(row.key, c ? cityResultToPlace(c) : null)}
        />
      </div>
      {multiple && (
        <button
          type="button"
          onClick={() => onRemove(row.key)}
          aria-label={`Remove ${label}`}
          style={{
            flexShrink: 0, width: 38, height: 38, borderRadius: 10, cursor: 'pointer',
            border: '1px solid rgba(20,40,30,0.12)', background: '#f4f6f3', color: '#6c7872', fontSize: 18,
          }}
        >
          ×
        </button>
      )}
    </div>
  );
};

const TripFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  // "Create copy" on a trip routes here with ?copyFrom=<tripId>. Nothing is
  // created until the user submits: the form simply opens pre-filled from that
  // trip, titled "Copy of X", so everything stays reviewable and editable
  // first. Copying a trip therefore needs no backend endpoint at all.
  const [searchParams] = useSearchParams();
  const copyFromId = !isEdit ? searchParams.get('copyFrom') : null;
  useDocumentTitle(isEdit ? 'Edit trip' : 'Plan a trip');
  const navigate = useNavigate();
  const { showToast } = useToast();
  // Anonymous access: /trips/new is public — a logged-out submit creates a
  // guest session first, then the trip (spec §2/§3).
  const { user } = useAuth();

  const [loading, setLoading] = useState(isEdit || !!copyFromId);
  const [saving, setSaving] = useState(false);
  // Edit flow: after saving changed parameters, a fresh Smart Mix alternative
  // is generated from the new brief; this drives the full-screen wait state.
  const [generating, setGenerating] = useState(false);
  // Cancel plumbing for that regeneration (same abort semantics as the trip
  // detail page: the server may still finish; it just surfaces on next load).
  const generateAbortRef = useRef<AbortController | null>(null);
  const [notFound, setNotFound] = useState(false);

  // Tier 2 (optional details). Collapsed on create so the first paint is the
  // three essentials; expanded on edit where title/stage live.
  const [detailsOpen, setDetailsOpen] = useState(isEdit || !!copyFromId);

  const [title, setTitle] = useState('');
  const [stage, setStage] = useState<TripStage>('idea');

  // When: either concrete dates or a rough duration preset — never both.
  const [whenMode, setWhenMode] = useState<'dates' | 'preset'>('dates');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [durationPreset, setDurationPreset] = useState<DurationPreset | null>(null);

  const destKeyRef = useRef(2);
  // One ordered, drag-reorderable list of ALL locations. locations[0] is the
  // origin (the "Start"); the rest are destinations. Saved back to params as
  // origin = locations[0], destinations = locations.slice(1). Starts with two
  // rows because a trip needs an origin plus at least one destination.
  const [locations, setLocations] = useState<DestRow[]>([{ key: 0, place: null }, { key: 1, place: null }]);
  // Pointer needs a small drag threshold so a click/tap on the grip doesn't
  // start a drag; keyboard sensor makes reordering accessible (Space then arrows).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  // Key of the destination row to auto-focus next (set when a row is added so
  // the user can keep adding stops without reaching for the mouse).
  const [focusKey, setFocusKey] = useState<number | null>(null);

  const [travelers, setTravelers] = useState(2);
  const [transportPreference, setTransportPreference] = useState('none');
  // A new trip starts with "Night train" picked too (Paulo 2026-09-30):
  // the option is on by default, and the traveller opts out. Off while the
  // backend's flag is off (below).
  const [sharedModes, setSharedModes] = useState<string[]>(['train', NIGHT_TRAIN_OPTION, 'bus', 'airplane']);
  // The "Night train" chip shows when the backend's flag says so, and always
  // for a brief that already has it (the flag may have been turned off since).
  const [nightTrainOption, setNightTrainOption] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetchNightTrainFormOption().then((on) => {
      if (cancelled) return;
      setNightTrainOption(on);
      // Flag off, new trip: the default must not save an option the form does not show.
      if (!on && !isEdit) setSharedModes((cur) => cur.filter((m) => m !== NIGHT_TRAIN_OPTION));
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const sharedChips = SHARED_CHIPS.filter((o) => o.value !== NIGHT_TRAIN_OPTION || nightTrainOption || sharedModes.includes(NIGHT_TRAIN_OPTION));
  const [roundTrip, setRoundTrip] = useState(true);
  const [pace, setPace] = useState<Pace>('balanced');
  // Single free-text brief (replaces the separate interests + notes fields;
  // saved to params.notes — the prompt treats both the same way).
  const [about, setAbout] = useState('');
  const [friendsToVisit, setFriendsToVisit] = useState<string[]>([]);

  const [friends, setFriends] = useState<Friend[]>([]);
  // Set when a submit fails the min-locations rule; empty location inputs are
  // highlighted red until enough places are picked (per-row via `!row.place`).
  const [locationsInvalid, setLocationsInvalid] = useState(false);
  const filledPlacesCount = locations.filter((r) => r.place).length;
  // A trip must pick some way to travel: a personal transport, or (when personal
  // is none/bicycle) at least one shared mode. Mirrors the generation rule.
  const transportInvalid = usesSharedTransport(transportPreference) && sharedModes.length === 0;
  const [showTransportError, setShowTransportError] = useState(false);

  const newDestRow = (place: TripPlace | null = null): DestRow => ({ key: destKeyRef.current++, place });

  // Load the user's friends for the "friends to visit" chips.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await authApi.get('/friends');
        if (active) setFriends(res.data?.friends ?? []);
      } catch {
        /* non-fatal — the form still works without the friends list */
      }
    })();
    return () => { active = false; };
  }, []);

  // Edit mode: hydrate the form from the trip's stored brief.
  const hydrate = useCallback((p: TripParams | null, t: { title: string; stage: TripStage; dateStart: string | null; dateEnd: string | null }) => {
    setTitle(t.title || '');
    setStage(t.stage || 'idea');
    setDateStart(toDateInput(t.dateStart));
    setDateEnd(toDateInput(t.dateEnd));
    if (!p) return;
    // A preset-only brief (no dates) reopens in preset mode.
    if (!t.dateStart && !t.dateEnd && p.durationPreset) {
      setWhenMode('preset');
      setDurationPreset(p.durationPreset);
    }
    // Rebuild the unified list: origin first, then destinations. Pad with
    // empty rows up to two so a legacy short brief still shows the full
    // origin-plus-destination shape the form now requires.
    const places: TripPlace[] = [];
    if (p.origin) places.push(p.origin);
    if (p.destinations && p.destinations.length) places.push(...p.destinations);
    if (places.length) {
      const rows = places.map((pl) => newDestRow(pl));
      while (rows.length < 2) rows.push(newDestRow());
      setLocations(rows);
    }
    if (typeof p.travelers === 'number') setTravelers(p.travelers);
    if (p.transportPreference) setTransportPreference(p.transportPreference);
    if (p.sharedTransportOptions) setSharedModes(p.sharedTransportOptions);
    if (typeof p.roundTrip === 'boolean') setRoundTrip(p.roundTrip);
    if (p.pace) setPace(p.pace);
    // Legacy briefs kept interests[] and notes separately; the v2 form merges
    // them into one text (interests first so nothing is lost on resave).
    const legacyInterests = Array.isArray(p.interests) && p.interests.length ? p.interests.join(', ') : '';
    setAbout([legacyInterests, p.notes || ''].filter(Boolean).join('\n'));
    if (p.friendsToVisit) setFriendsToVisit(p.friendsToVisit);
  }, []);

  useEffect(() => {
    const sourceId = isEdit ? id : copyFromId;
    if (!sourceId) return;
    let active = true;
    (async () => {
      try {
        const detail = await fetchTripDetail(sourceId);
        if (!active) return;
        // Copying reuses the source's settings but never its identity: a fresh
        // title, and the lifecycle stage restarts at Idea, since a copy has not
        // been organized or booked.
        hydrate(detail.params, isEdit
          ? detail
          : { ...detail, title: `Copy of ${detail.title}`, stage: 'idea' });
      } catch {
        // Editing a trip that cannot be loaded is a dead end, so say so. A
        // failed COPY is not: fall through to the blank create form rather
        // than blocking the user from making a trip at all.
        if (active && isEdit) setNotFound(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id, isEdit, copyFromId, hydrate]);

  // --- Unsaved-changes guard --------------------------------------------------
  // A serialized snapshot of the meaningful fields (empty destination rows are
  // ignored). The baseline is captured once the form is ready, so the edit-mode
  // hydrate doesn't read as a user change.
  const formSnapshot = (): string =>
    JSON.stringify({
      title: title.trim(),
      stage,
      whenMode,
      dateStart,
      dateEnd,
      durationPreset,
      locations: locations
        .map((r) => r.place)
        .filter((p): p is TripPlace => !!p)
        .map((p) => ({ c: p.city, la: p.latitude, lo: p.longitude })),
      travelers,
      transportPreference,
      sharedModes,
      roundTrip,
      pace,
      about: about.trim(),
      friendsToVisit,
    });

  // Like formSnapshot, but only the fields that feed journey GENERATION
  // (everything except title/stage). Deciding whether an edit warrants a
  // fresh Smart Mix compares this, so a pure rename or stage change doesn't
  // trigger a new AI route.
  const paramsSnapshot = (): string =>
    JSON.stringify({
      whenMode,
      dateStart,
      dateEnd,
      durationPreset,
      locations: locations
        .map((r) => r.place)
        .filter((p): p is TripPlace => !!p)
        .map((p) => ({ c: p.city, la: p.latitude, lo: p.longitude })),
      travelers,
      transportPreference,
      sharedModes,
      roundTrip,
      pace,
      about: about.trim(),
      friendsToVisit,
    });

  const baselineRef = useRef<string | null>(null);
  const baselineParamsRef = useRef<string | null>(null);
  useEffect(() => {
    if (!loading && baselineRef.current === null) {
      baselineRef.current = formSnapshot();
      baselineParamsRef.current = paramsSnapshot();
    }
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty = baselineRef.current !== null && formSnapshot() !== baselineRef.current;
  const dirtyRef = useRef(false);
  dirtyRef.current = isDirty;
  const savedRef = useRef(false); // set on a successful save so we don't prompt
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // --- "More form below" affordance -------------------------------------------
  // The sticky CTA creates a false floor: users don't realize the form
  // continues under it. When there's meaningful content below the viewport, a
  // small chevron floats above the CTA; tapping it scrolls the rest into view.
  const [moreBelow, setMoreBelow] = useState(false);
  const checkMoreBelow = () => {
    const remaining = document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
    setMoreBelow(remaining > 120);
  };
  useEffect(() => {
    window.addEventListener('scroll', checkMoreBelow, { passive: true });
    window.addEventListener('resize', checkMoreBelow);
    return () => {
      window.removeEventListener('scroll', checkMoreBelow);
      window.removeEventListener('resize', checkMoreBelow);
    };
  }, []);
  // Content height changes with almost any state (rows added, tier opened,
  // date/preset switch), so re-measure after every render. Same-value setState
  // is a no-op, so this doesn't loop.
  useEffect(checkMoreBelow);

  // Warn on tab close / refresh / external navigation while there are edits.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirtyRef.current && !savedRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // Back button: confirm first if there are unsaved edits.
  const handleBack = () => {
    if (isDirty && !savedRef.current) setShowLeaveConfirm(true);
    else navigate(-1);
  };

  const setDestPlace = (key: number, place: TripPlace | null) =>
    setLocations((rows) => rows.map((r) => (r.key === key ? { ...r, place } : r)));
  // Append a location (the "Add a place" button) and focus it.
  const addDest = () => {
    const row = newDestRow();
    setLocations((rows) => [...rows, row]);
    setFocusKey(row.key);
  };
  // Insert a location right after `index` and focus it (keyboard flow).
  const insertDestAfter = (index: number) => {
    const row = newDestRow();
    setLocations((rows) => {
      const next = [...rows];
      next.splice(index + 1, 0, row);
      return next;
    });
    setFocusKey(row.key);
  };
  // Enter / Tab in a location field adds the next stop without the mouse.
  // Enter that the autocomplete already consumed (to pick a city) is ignored.
  const onDestKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number, row: DestRow) => {
    if (e.key === 'Enter') {
      if (e.defaultPrevented) return; // CitySearchInput used Enter to pick a city
      e.preventDefault(); // don't submit the form
      insertDestAfter(index);
    } else if (e.key === 'Tab' && !e.shiftKey) {
      // Only extend from the last, already-filled row — so Tab still escapes the
      // list normally from an empty trailing field.
      if (index === locations.length - 1 && row.place) {
        e.preventDefault();
        insertDestAfter(index);
      }
    }
  };
  const removeDest = (key: number) =>
    setLocations((rows) => (rows.length <= 1 ? rows : rows.filter((r) => r.key !== key)));
  // Drag-and-drop reorder via @dnd-kit. Stable row keys keep each
  // CitySearchInput's state attached to its location as rows move.
  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setLocations((rows) => {
      const from = rows.findIndex((r) => r.key === active.id);
      const to = rows.findIndex((r) => r.key === over.id);
      return from < 0 || to < 0 ? rows : arrayMove(rows, from, to);
    });
  };

  // --- Transport chip rules ---------------------------------------------------
  const pickSharedMode = (mode: string) => {
    setShowTransportError(false);
    if (MOTORISED_PERSONAL.includes(transportPreference)) {
      // Switching back from a personal vehicle: the tapped mode starts the set.
      setTransportPreference('none');
      setSharedModes([mode]);
      return;
    }
    setSharedModes((cur) => (cur.includes(mode) ? cur.filter((m) => m !== mode) : [...cur, mode]));
  };
  const pickPersonalMode = (mode: string) => {
    setShowTransportError(false);
    if (transportPreference === mode) {
      setTransportPreference('none'); // tap again to deselect
      return;
    }
    setTransportPreference(mode);
    if (MOTORISED_PERSONAL.includes(mode)) setSharedModes([]);
  };

  const switchToPreset = () => {
    setWhenMode('preset');
    setDateStart('');
    setDateEnd('');
  };
  const switchToDates = () => {
    setWhenMode('dates');
    setDurationPreset(null);
  };

  const toggleFriend = (friendId: string) =>
    setFriendsToVisit((cur) => (cur.includes(friendId) ? cur.filter((f) => f !== friendId) : [...cur, friendId]));

  // The "Jody Smart Mix" strategy for this brief: multimodal when several
  // shared modes are allowed, otherwise the one allowed mode (same derivation
  // the trip detail page's strategy cards use).
  const smartStrategy = (): Strategy => {
    const strategies = allowedStrategies(buildParams());
    return strategies.find((s) => s.kind === 'multimodal') ?? strategies[0] ?? { kind: 'multimodal' };
  };

  const buildParams = (): TripParams => {
    // The unified list is [origin, ...destinations]: first place is the origin.
    const places = locations.map((r) => r.place).filter((p): p is TripPlace => !!p);
    return {
      origin: places[0] ?? undefined,
      destinations: places.slice(1),
      durationPreset: whenMode === 'preset' && durationPreset ? durationPreset : undefined,
      travelers: travelers || undefined,
      transportPreference,
      // Only meaningful when the traveller uses shared transport.
      sharedTransportOptions: usesSharedTransport(transportPreference) ? sharedModes : [],
      roundTrip,
      pace,
      // v2 folds interests into the single free-text brief (params.notes).
      interests: [],
      friendsToVisit,
      notes: about.trim() || undefined,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const places = locations.map((r) => r.place).filter((p): p is TripPlace => !!p);
    // A trip is a route: origin plus at least one destination (the backend
    // enforces the same rule). The first filled row counts as the origin.
    if (places.length < 2) {
      setLocationsInvalid(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      showToast({ title: 'Missing details', message: 'Add where you start and at least one destination.', type: 'error', duration: 5000 });
      return;
    }
    // The title is auto-derived from the route when left empty.
    const trimmedTitle = title.trim() || deriveTitle(places);
    if (transportInvalid) {
      setShowTransportError(true);
      showToast({ title: 'Pick a transport', message: 'Select at least one way to travel.', type: 'error', duration: 5000 });
      return;
    }
    if (whenMode === 'dates' && !isDateRangeValid(dateStart, dateEnd)) {
      showToast({ title: 'Check the dates', message: DATE_RANGE_MESSAGE, type: 'error', duration: 5000 });
      return;
    }

    setSaving(true);
    const payload = {
      title: trimmedTitle,
      stage,
      dateStart: (whenMode === 'dates' && dateStart) || null,
      dateEnd: (whenMode === 'dates' && dateEnd) || null,
      params: buildParams(),
    };
    // Did this edit change anything the generator cares about? (Rename/stage
    // tweaks don't; they skip the regeneration below.)
    const paramsChanged = baselineParamsRef.current !== null && paramsSnapshot() !== baselineParamsRef.current;
    try {
      // Logged out: start (or reuse) the guest session before the first trip.
      if (!user && !isEdit) await ensureGuestSession();
      const trip = isEdit && id ? await updateTrip(id, payload) : await createTrip(payload);
      if (!user && !isEdit) setGuestTripId(trip.id);
      savedRef.current = true; // saved → don't warn on the navigation below
      showToast({
        title: 'Saved',
        message: isEdit ? 'Trip updated.' : 'Trip created.',
        type: 'success',
        duration: 3000,
      });
      if (!isEdit) {
        // On create, ask the trip detail page to auto-generate the initial
        // alternative set so the new trip is never an empty dead-end.
        navigate(`/trips/${encodeURIComponent(trip.id)}?generate=default`);
        return;
      }
      if (paramsChanged) {
        // The brief changed: generate a fresh Smart Mix from the new
        // parameters and land the user directly on the new route.
        const controller = new AbortController();
        generateAbortRef.current = controller;
        setGenerating(true);
        try {
          const created = await generateAlternative(trip.id, smartStrategy(), controller.signal);
          navigate(`/journeys/${encodeURIComponent(created.id)}`);
          return;
        } catch (genErr: any) {
          // User hit Cancel on the overlay — handleCancelGeneration already
          // toasted and navigated to the trip page.
          if (isAbortError(genErr)) return;
          setGenerating(false);
          showToast({
            title: 'Route not generated',
            message: 'Your changes are saved, but Jody could not plan a new route. Try again from the trip page.',
            type: 'error',
            duration: 6000,
          });
          // fall through to the trip page below
        } finally {
          generateAbortRef.current = null;
        }
      }
      navigate(`/trips/${encodeURIComponent(trip.id)}`);
    } catch (err: any) {
      // A guest who already used their free trip gets the signup wall, not an
      // error toast — the backend message carries the curated copy.
      if (err?.response?.data?.code === 'GUEST_TRIP_LIMIT') {
        openGuestWall(err.response.data.error);
        return;
      }
      showToast({
        title: 'Error',
        message: err?.response?.data?.error || err?.message || 'Failed to save the trip.',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  // Cancel the post-edit regeneration from the loading overlay. The trip edits
  // themselves are already saved, so land on the trip page; the catch guard in
  // handleSubmit (isAbortError) skips the error toast.
  const handleCancelGeneration = () => {
    generateAbortRef.current?.abort();
    generateAbortRef.current = null;
    setGenerating(false);
    showToast({
      title: 'Generation stopped',
      message: 'Your changes are saved. You can plan a new route from the trip page.',
      type: 'info',
      duration: 4000,
    });
    if (id) navigate(`/trips/${encodeURIComponent(id)}`);
  };

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100%' }}>
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '20px 16px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={handleBack}
            aria-label="Back"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 36, height: 36, borderRadius: 999, border: 'none', background: '#ffffff',
              color: '#16241d', boxShadow: '0 1px 3px rgba(20,40,30,0.10)', cursor: 'pointer', flexShrink: 0,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M9 2 L4 7 L9 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <h1 style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 22, fontWeight: 600, color: '#16241d', margin: 0 }}>
            {isEdit ? 'Edit trip' : 'New trip'}
          </h1>
        </div>
        <p style={{ color: '#6c7872', fontSize: 13.5, margin: '8px 0 18px', lineHeight: 1.45 }}>
          The basics take 30 seconds. Jody plans a full journey from them, and you can fine-tune everything later.
        </p>

        {loading ? (
          <div style={{ color: '#6c7872', padding: '24px 0' }}>Loading trip…</div>
        ) : notFound ? (
          <div style={{ color: '#6c7872', padding: '24px 0' }}>Trip not found.</div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* ---- Tier 1 · Where ---- */}
            <SectionCard
              eyebrow="Where"
              icon={(
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-6.1-7-11a7 7 0 0 1 14 0c0 4.9-7 11-7 11z" /><circle cx="12" cy="10" r="2.6" /></svg>
              )}
              question="Where do you want to go?"
            >
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={locations.map((r) => r.key)} strategy={verticalListSortingStrategy}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {locations.map((row, idx) => (
                      <SortableDestRow
                        key={row.key}
                        row={row}
                        index={idx}
                        isFirst={idx === 0}
                        multiple={locations.length > 1}
                        invalid={locationsInvalid && filledPlacesCount < 2 && !row.place}
                        focusKey={focusKey}
                        onKeyDown={onDestKeyDown}
                        onSelectPlace={setDestPlace}
                        onRemove={removeDest}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              <button
                type="button"
                onClick={addDest}
                style={{
                  marginTop: 8, padding: '6px 12px', borderRadius: 10, cursor: 'pointer',
                  border: '1.5px dashed rgba(20,40,30,0.25)', background: 'transparent', color: '#157a55',
                  fontSize: 13, fontWeight: 600,
                }}
              >
                ＋ Add a place
              </button>
              {locationsInvalid && filledPlacesCount < 2 ? (
                <p role="alert" style={{ ...hintStyle, color: '#c0392b' }}>
                  Add where you start and at least one destination.
                </p>
              ) : (
                <p style={hintStyle}>A city or a whole country works. Jody adds stops to fit your pace.</p>
              )}

              {/* Round trip lives in tier 1: a default hidden inside the
                  collapsed details panel is one the user never sees
                  (2026.08.23 handover §3 A1). */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '4px 0', marginTop: 12 }}>
                <div>
                  <p style={{ ...groupLabelStyle, margin: 0 }}>Round trip</p>
                  <p style={{ ...hintStyle, marginTop: 2 }}>
                    {locations[0]?.place?.city
                      ? `End the journey back in ${locations[0].place.city}`
                      : 'End the journey back where you started'}
                  </p>
                </div>
                <Switch on={roundTrip} onToggle={() => setRoundTrip((v) => !v)} ariaLabel="Round trip" />
              </div>
            </SectionCard>

            {/* ---- Tier 1 · When ---- */}
            <SectionCard
              eyebrow="When"
              icon={(
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18" /></svg>
              )}
            >
              {whenMode === 'dates' ? (
                <>
                  <DateRangeFields
                    layout="stack"
                    start={dateStart}
                    end={dateEnd}
                    onChange={({ start, end }) => { setDateStart(start); setDateEnd(end); }}
                  />
                  <button type="button" style={{ ...linkyStyle, marginTop: 2 }} onClick={switchToPreset}>
                    Not sure yet?
                  </button>
                </>
              ) : (
                <>
                  <p style={groupLabelStyle}>Roughly how long?</p>
                  <ChipRow>
                    {DURATION_PRESET_OPTIONS.map((o) => (
                      <Chip
                        key={o.value}
                        label={o.label}
                        active={durationPreset === o.value}
                        onClick={() => setDurationPreset((cur) => (cur === o.value ? null : o.value))}
                      />
                    ))}
                  </ChipRow>
                  <button type="button" style={{ ...linkyStyle, marginTop: 10, display: 'inline-block' }} onClick={switchToDates}>
                    I know my dates
                  </button>
                </>
              )}
            </SectionCard>

            {/* ---- Tier 1 · Getting around ---- */}
            <SectionCard
              eyebrow="Getting around"
              icon={(
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="3" width="16" height="14" rx="2" /><path d="M4 11h16M8 21l2-4M16 21l-2-4" /></svg>
              )}
              question="How do you like to travel?"
            >
              <ChipRow>
                {sharedChips.map((o) => (
                  <Chip
                    key={o.value}
                    label={o.label}
                    emoji={o.emoji}
                    active={usesSharedTransport(transportPreference) && sharedModes.includes(o.value)}
                    onClick={() => pickSharedMode(o.value)}
                  />
                ))}
                {PERSONAL_CHIPS.map((o) => (
                  <Chip
                    key={o.value}
                    label={o.label}
                    emoji={o.emoji}
                    active={transportPreference === o.value}
                    onClick={() => pickPersonalMode(o.value)}
                  />
                ))}
              </ChipRow>
              {showTransportError && transportInvalid ? (
                <p style={{ ...hintStyle, color: '#c0392b' }}>Select at least one way to travel.</p>
              ) : (
                <p style={hintStyle}>
                  Pick everything you would consider. Your own vehicle (car, camper van, motorcycle) replaces shared transport.
                </p>
              )}
            </SectionCard>

            {/* ---- Tier 2 · Optional details ---- */}
            <button
              type="button"
              onClick={() => setDetailsOpen((o) => !o)}
              aria-expanded={detailsOpen}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 10, background: '#ffffff', border: 'none', borderRadius: 16, padding: 16,
                cursor: 'pointer', textAlign: 'left', marginBottom: 12,
              }}
            >
              <span>
                <span style={{ display: 'block', fontFamily: 'Fraunces, Georgia, serif', fontSize: 16, fontWeight: 600, color: '#16241d' }}>
                  Add details for a better plan
                </span>
                <span style={{ display: 'block', fontSize: 12.5, color: '#6c7872', marginTop: 3 }}>
                  All optional. Skip it and Jody uses smart defaults.
                </span>
              </span>
              <svg
                width="16" height="16" viewBox="0 0 16 16" fill="none"
                style={{ flexShrink: 0, color: '#6c7872', transform: detailsOpen ? 'rotate(180deg)' : 'none', transition: 'transform .18s' }}
              >
                <path d="M3 6l5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {detailsOpen && (
              <div style={{ background: '#ffffff', borderRadius: 16, padding: 16, marginBottom: 12 }}>
                <FormField label="Trip title" hint={isEdit ? undefined : 'leave empty and Jody names it from your route'}>
                  <Input
                    value={title}
                    placeholder="e.g. Alpine rail loop"
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </FormField>

                <FormField label="Stage" hint="where this trip is in your planning">
                  <Dropdown
                    ariaLabel="Stage"
                    options={STAGE_OPTIONS}
                    value={stage}
                    onChange={(v) => setStage(v as TripStage)}
                  />
                </FormField>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '4px 0', marginBottom: 14 }}>
                  <div>
                    <p style={{ ...groupLabelStyle, margin: 0 }}>Travelers</p>
                    <p style={{ ...hintStyle, marginTop: 2 }}>Who is coming along?</p>
                  </div>
                  <Stepper value={travelers} min={1} max={12} onChange={setTravelers} ariaLabel="Travelers" />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <p style={{ ...groupLabelStyle, display: 'flex', alignItems: 'center', gap: 5 }}>
                    Pace
                    <Tooltip
                      bare
                      widthClassName="w-60"
                      content={(
                        <>
                          How long you like to stay in each place. Jody plans the number of stops from it:
                          {PACE_OPTIONS.map((o) => (
                            <span key={o.value} style={{ display: 'block', marginTop: 4 }}>
                              <b>{o.label}:</b> about {o.nights} nights per place
                            </span>
                          ))}
                        </>
                      )}
                    >
                      <button
                        type="button"
                        aria-label="What pace means"
                        style={{ display: 'inline-grid', placeItems: 'center', width: 16, height: 16, padding: 0, border: 'none', background: 'transparent', color: '#6c7872', cursor: 'help' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
                      </button>
                    </Tooltip>
                  </p>
                  <ChipRow>
                    {PACE_OPTIONS.map((o) => (
                      <Chip key={o.value} label={o.label} active={pace === o.value} onClick={() => setPace(o.value)} />
                    ))}
                  </ChipRow>
                </div>

                <div style={{ marginBottom: 14 }}>
                  <p style={groupLabelStyle}>Anything Jody should know?</p>
                  <Textarea
                    value={about}
                    rows={3}
                    placeholder="e.g. we love food markets and hiking, no early starts, traveling with a toddler"
                    onChange={(e) => setAbout(e.target.value)}
                  />
                </div>

                <div>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                    <p style={{ ...groupLabelStyle, margin: 0 }}>Friends to visit along the way</p>
                    {/* New tab on purpose: an in-app navigation would silently
                        drop the half-filled form (the guard only covers
                        beforeunload, not route changes). */}
                    <a
                      href="/friends"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ ...linkyStyle, fontSize: 12.5, flexShrink: 0 }}
                    >
                      Manage friends
                    </a>
                  </div>
                  {friends.length > 0 ? (
                    <ChipRow>
                      {friends.map((f) => (
                        <Chip
                          key={f.id}
                          label={`${f.name}${f.city ? ` · ${f.city}` : ''}`}
                          active={friendsToVisit.includes(f.id)}
                          onClick={() => toggleFriend(f.id)}
                        />
                      ))}
                    </ChipRow>
                  ) : (
                    <p style={{ ...hintStyle, marginTop: 0 }}>
                      No friends saved yet. Add the people you would visit and Jody routes past them.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Sticky CTA: stays in reach while the (long) form scrolls. */}
            <div
              style={{
                position: 'sticky', bottom: 0, paddingTop: 12, paddingBottom: 14,
                background: 'linear-gradient(to top, hsl(var(--jody-paper)) 72%, hsl(var(--jody-paper) / 0))',
              }}
            >
              {moreBelow && (
                <button
                  type="button"
                  aria-label="Scroll to the rest of the form"
                  onClick={() => {
                    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: reduced ? 'auto' : 'smooth' });
                  }}
                  style={{
                    position: 'absolute', top: -26, left: '50%', transform: 'translateX(-50%)',
                    width: 36, height: 36, borderRadius: '50%', border: 'none',
                    background: '#ffffff', color: '#157a55', cursor: 'pointer',
                    boxShadow: '0 2px 10px rgba(20,40,30,0.20)',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 6l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              )}
              <Button type="submit" variant="greenPrimary" disabled={saving} style={{ width: '100%' }}>
                {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create my journey'}
              </Button>
              {!isEdit && (
                <p style={{ textAlign: 'center', fontSize: 12, color: '#6c7872', margin: '8px 0 0' }}>
                  You can change everything later.
                </p>
              )}
            </div>
          </form>
        )}
      </div>

      {/* Full-screen wait while the post-edit Smart Mix generates; the user
          lands on the new journey when it's ready. Same unified overlay as the
          other LLM waits (trip detail / planner / journey editor). */}
      <LLMLoadingSpinner
        message="Planning your new route…"
        onCancel={handleCancelGeneration}
        isVisible={generating}
      />

      {showLeaveConfirm && (
        <ConfirmationDialog
          title="Discard changes?"
          message="You have unsaved changes to this trip. Leave without saving?"
          confirmText="Discard and leave"
          cancelText="Keep editing"
          confirmButtonStyle="danger"
          onConfirm={() => { setShowLeaveConfirm(false); navigate(-1); }}
          onCancel={() => setShowLeaveConfirm(false)}
        />
      )}
    </div>
  );
};

export default TripFormPage;
